import { matchesTallySearch } from '../types'

export type FleetStatus = 'active' | 'inactive' | 'maintenance' | 'archived'

export type FleetMaintenanceLog = {
  _id?: string
  date: string
  mileage?: number | null
  name: string
  description?: string | null
  vendor?: string | null
  price?: number | null
  invoiceImageUrls?: string[]
  createdBy?: string | null
  createdAt?: string
}

export type FleetHistoryEntry = {
  _id?: string
  action: string
  note?: string | null
  userName?: string | null
  changes?: unknown
  createdAt?: string
}

export type FleetUnit = {
  _id: string
  vin: string
  name: string
  year?: number | null
  make?: string | null
  model?: string | null
  status?: FleetStatus | string
  archivedAt?: string | null
  archivedBy?: string | null
  assignedTo?: string | null
  assignedEmployeeId?: string | null
  /** Resolved Contact display name for assignedEmployeeId (API enrichment). */
  assignedContactName?: string | null
  notes?: string | null
  imageUrls?: string[]
  registrationImageUrls?: string[]
  insuranceImageUrls?: string[]
  location?: {
    type?: string
    coordinates?: number[]
    address?: string
  }
  purchasedAt?: string | null
  purchasePrice?: number | null
  purchaseVendor?: string | null
  purchaseNotes?: string | null
  externalSource?: string | null
  externalKey?: string | null
  externalIdentifiers?: {
    vin?: string
    serialNumber?: string
    imei?: string
    deviceUUID?: string
    trackerId?: string
  }
  linxup?: Record<string, unknown> | null
  lastSyncedAt?: string | null
  lastSeenAt?: string | null
  maintenanceLogs?: FleetMaintenanceLog[]
  history?: FleetHistoryEntry[]
  createdAt?: string
  updatedAt?: string
}

export type FleetPanelTab = 'info' | 'compliance' | 'maintenance' | 'history'

export function formatYearMakeModel(unit: Pick<FleetUnit, 'year' | 'make' | 'model'>): string {
  const parts = [unit.year, unit.make, unit.model].filter((p) => p !== null && p !== undefined && String(p).trim())
  return parts.length ? parts.join(' ') : '—'
}

/** GeoJSON on Fleet is [lng, lat]. Also accept Linxup latitude/longitude on the unit snapshot. */
export function getFleetLatLng(unit: FleetUnit): { lat: number; lng: number } | null {
  const coords = unit.location?.coordinates
  if (Array.isArray(coords) && coords.length >= 2) {
    const lng = Number(coords[0])
    const lat = Number(coords[1])
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng }
    }
  }
  const lx = unit.linxup && typeof unit.linxup === 'object' ? (unit.linxup as Record<string, unknown>) : null
  if (lx) {
    const lat = Number(lx.latitude ?? lx.lat)
    const lng = Number(lx.longitude ?? lx.lng ?? lx.lon)
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng }
    }
    const combined = lx.latLng ?? lx.latLong ?? lx.latlog ?? lx.latLon
    if (typeof combined === 'string' && combined.includes(',')) {
      const [a, b] = combined.split(',').map((p) => Number(String(p).trim()))
      if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(a) <= 90 && Math.abs(b) <= 180) {
        return { lat: a, lng: b }
      }
    }
  }
  return null
}

export function formatFleetCoords(latLng: { lat: number; lng: number }): string {
  return `${latLng.lat.toFixed(5)}, ${latLng.lng.toFixed(5)}`
}

export function formatFleetLocationLabel(unit: FleetUnit): string | null {
  const address = String(unit.location?.address || '').trim()
  const latLng = getFleetLatLng(unit)
  if (address && latLng) return `${address} (${formatFleetCoords(latLng)})`
  if (address) return address
  if (latLng) return formatFleetCoords(latLng)
  return null
}

export function googleMapsUrlForFleet(unit: FleetUnit): string | null {
  const latLng = getFleetLatLng(unit)
  if (!latLng) return null
  return `https://www.google.com/maps?q=${encodeURIComponent(`${latLng.lat},${latLng.lng}`)}`
}

export function buildFleetSearchHaystack(unit: FleetUnit): string {
  const ids = unit.externalIdentifiers || {}
  const linxup = unit.linxup && typeof unit.linxup === 'object' ? unit.linxup : {}
  const linxupParts = Object.values(linxup).flatMap((v) => {
    if (v == null) return []
    if (typeof v === 'object') return [JSON.stringify(v)]
    return [String(v)]
  })
  const maintenanceParts = (unit.maintenanceLogs || []).flatMap((log) => [
    log.name,
    log.description,
    log.vendor,
    log.mileage,
    log.price,
  ])
  const parts: Array<string | number | null | undefined> = [
    unit.name,
    unit.vin,
    unit.year,
    unit.make,
    unit.model,
    unit.status,
    unit.notes,
    unit.assignedTo,
    unit.assignedEmployeeId,
    unit.assignedContactName,
    unit.location?.address,
    unit.purchaseVendor,
    unit.purchaseNotes,
    unit.purchasePrice,
    unit.externalSource,
    unit.externalKey,
    ids.vin,
    ids.serialNumber,
    ids.imei,
    ids.deviceUUID,
    ids.trackerId,
    ...linxupParts,
    ...maintenanceParts,
  ]
  return parts
    .filter((p) => p !== null && p !== undefined && String(p).trim() !== '')
    .join(' ')
    .toLowerCase()
}

export function fleetMatchesSearch(unit: FleetUnit, queries: string[]): boolean {
  if (!queries.length) return true
  const haystack = buildFleetSearchHaystack(unit)
  return queries.every((q) => matchesTallySearch(haystack, q))
}

export function formatFleetMoney(value?: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—'
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(value))
}

export function formatFleetDate(value?: string | Date | null): string {
  if (!value) return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString()
}

export function formatFleetDateTime(value?: string | Date | null): string {
  if (!value) return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString()
}

export function formatHistoryAction(action?: string | null): string {
  if (!action) return 'Event'
  return action.replace(/_/g, ' ')
}
